import { useState } from "react";
import api from "../../lib/api";

/**
 * STARSEED-CAPMARKETS-173 — record the partner accounts for this member.
 *
 * A SEPARATE panel from `DecisionPanel`, and not for cosmetic reasons.
 *
 * `DecisionPanel` hides itself once the agreement is countersigned or rejected,
 * because a decision is a per-document legal act that is not repeatable. If
 * attaching lived inside it, the field would DISAPPEAR the moment FinBPM
 * countersigns — so an operator who creates the accounts afterwards, which is
 * the common case, would have nowhere to type them. And a field on the decision
 * form is empty whenever the refs are not yet known.
 *
 * The 172 index is explicit that no part of this pack may assume an order: the
 * capture path has to work whether the refs arrive before, during or after
 * countersign. This panel is therefore always visible for an in-scope
 * participant, and says so.
 */
const AccountsPanel = ({ userId, onAttached }) => {
  const [refs, setRefs] = useState("");
  const [label, setLabel] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [attached, setAttached] = useState(null);

  // One field, split on commas/whitespace: members hold two accounts and
  // "989761, 989763" is how a human writes that. The server refuses a repeat
  // rather than silently deduplicating, so a typo surfaces instead of being
  // reported as two attaches.
  const parsed = refs
    .split(/[\s,]+/)
    .map((r) => r.trim())
    .filter(Boolean);

  // STARSEED-CAPMARKETS-175 — check, show, then commit.
  //
  // Refusing an account the partner has never heard of catches a typo that
  // produces a NON-existent number. It cannot catch a typo that produces a
  // real one: `989671` for `989761` is somebody else's account and passes
  // every check we have. So the operator is shown the partner's own label and
  // balance for what they typed, and confirms it is the account they meant.
  const [preview, setPreview] = useState(null);
  // Set only after the server has said it could not reach the partner. Never
  // offered up front — an override available before it is needed is a
  // fail-open switch with extra steps.
  const [canOverride, setCanOverride] = useState(false);

  const body = (extra) => ({
    refs: parsed,
    // Guarded as well as hidden: the field can hold a value from before
    // a second ref was typed, and hiding an input does not clear it.
    label: parsed.length === 1 ? label || null : null,
    note: note || null,
    ...extra,
  });

  const call = async (extra) => {
    setSubmitting(true);
    setError(null);
    try {
      // Encoded, like `ActivityTimeline`. `userId` reaches this component from
      // a URL param, so an unencoded value with a slash or `?` in it would
      // change which path is requested rather than failing — and this endpoint
      // writes who owns money. Three sibling call sites here still interpolate
      // raw; they are not this PR's to change, but this one should not add a
      // fourth. (Copilot, PR #2453.)
      return await api.post(
        `/partner/console/participants/${encodeURIComponent(userId)}/accounts`,
        body(extra)
      );
    } finally {
      setSubmitting(false);
    }
  };

  const onFailure = (e) => {
    const detail = e.response?.data?.detail;
    setPreview(null);
    setCanOverride(Boolean(detail?.can_override));
    setError(
      (typeof detail === "string" && detail) ||
        detail?.message ||
        "The accounts could not be attached. Nothing was saved."
    );
  };

  const check = async () => {
    try {
      const r = await call({ dry_run: true });
      const rows = r.data?.would_attach;
      // An EMPTY preview is a dead end, not a preview. `[]` is truthy in
      // JavaScript, so setting it hid the "Check" button (rendered on
      // `!preview`) AND the confirm block (rendered on `preview.length > 0`),
      // leaving the operator with no button at all and no way forward but to
      // retype the field. A response that describes nothing is a failure to
      // check, and must read as one. (Copilot, PR #2453.)
      if (!Array.isArray(rows) || rows.length === 0) {
        setPreview(null);
        setCanOverride(false);
        setError(
          "The check came back with nothing to confirm. Nothing was attached — try again."
        );
        return;
      }
      setPreview(rows);
      setCanOverride(false);
    } catch (e) {
      onFailure(e);
    }
  };

  const commit = async (acknowledgeUnvalidated = false) => {
    try {
      const r = await call(
        acknowledgeUnvalidated ? { acknowledge_unvalidated: true } : {}
      );
      setAttached(r.data?.attached_count ?? parsed.length);
      setPreview(null);
      setCanOverride(false);
      setRefs("");
      setLabel("");
      setNote("");
      if (onAttached) onAttached();
    } catch (e) {
      onFailure(e);
    }
  };

  return (
    <div className="mt-4 rounded-lg border border-slate-200 bg-white p-5">
      <h3 className="text-sm font-semibold text-slate-900">Partner accounts</h3>
      <p className="mt-1 text-sm text-slate-600">
        The account numbers you opened for this member. You can record these
        before or after countersigning — whichever order you work in.
      </p>

      {attached !== null && (
        <p
          className="mt-3 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-800"
          data-testid="attach-success"
        >
          Attached {attached} account{attached === 1 ? "" : "s"}.
        </p>
      )}

      <label className="mt-4 block text-sm">
        <span className="font-medium text-slate-800">Account number(s)</span>
        <input
          className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2"
          placeholder="989761, 989763"
          value={refs}
          data-testid="attach-refs"
          onChange={(e) => {
            setError(null);
            setAttached(null);
            // A preview describes the refs it was run for. Keeping it across an
            // edit would let the operator confirm an account they were shown
            // for different input — the same rule as CAPMARKETS-194's screen.
            setPreview(null);
            setCanOverride(false);
            setRefs(e.target.value);
          }}
        />
        <span className="text-xs text-slate-500">
          The account login, not the display name — a console name can be
          renamed, and the history would not follow.
        </span>
      </label>

      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {/*
          One console name cannot describe two accounts. Attaching 989761 and
          989763 with "JC CAP 1" stamped that name on both, so at least one
          label was false — and the label is the evidence CAPMARKETS-189 put in
          front of an operator deciding attribution, where a wrong one is worse
          than none. The field is offered only for a single ref.
          (`/code-review`, PR #2453.)
        */}
        {parsed.length > 1 ? (
          <div className="block text-sm" data-testid="attach-label-hidden">
            <span className="font-medium text-slate-800">Console name</span>
            <p className="mt-1 text-xs text-slate-500">
              Not recorded for several accounts at once — one name cannot
              describe two. Attach them one at a time if you want the names
              saved.
            </p>
          </div>
        ) : (
          <label className="block text-sm">
            <span className="font-medium text-slate-800">Console name (optional)</span>
            <input
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2"
              placeholder="JC CAP 1"
              value={label}
              data-testid="attach-label"
              onChange={(e) => setLabel(e.target.value)}
            />
          </label>
        )}
        <label className="block text-sm">
          <span className="font-medium text-slate-800">Note (optional)</span>
          <input
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2"
            placeholder="why these accounts are this member's"
            value={note}
            data-testid="attach-note"
            onChange={(e) => setNote(e.target.value)}
          />
        </label>
      </div>

      {error && (
        <p
          className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700"
          data-testid="attach-error"
        >
          {error}
        </p>
      )}

      {canOverride && (
        <button
          type="button"
          className="mt-3 ml-0 rounded-md border border-amber-400 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-900"
          disabled={submitting}
          data-testid="attach-override"
          onClick={() => commit(true)}
        >
          Attach without checking — I am certain of these numbers
        </button>
      )}

      {preview && preview.length > 0 && (
        <div
          className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-4"
          data-testid="attach-preview"
        >
          <p className="text-sm font-medium text-slate-900">
            Confirm these are the right accounts
          </p>
          <p className="mt-1 text-xs text-slate-600">
            We checked these numbers exist. We cannot check whose they are —
            that is your judgement, and it is the one thing this step cannot
            do for you.
          </p>
          <ul className="mt-3 space-y-2">
            {preview.map((row) => (
              <li key={row.partner_account_ref} className="text-sm text-slate-800">
                <span className="font-mono">{row.partner_account_ref}</span>
                {row.account?.partner_label ? ` — ${row.account.partner_label}` : ""}
                {typeof row.account?.balance === "number"
                  ? ` — ${row.account.balance.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}`
                  : ""}
              </li>
            ))}
          </ul>
          <button
            type="button"
            className="mt-3 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            disabled={submitting}
            data-testid="attach-confirm"
            onClick={() => commit(false)}
          >
            {submitting ? "Attaching…" : "Yes, attach these"}
          </button>
        </div>
      )}

      {!preview && (
        <button
          type="button"
          className="mt-4 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          disabled={submitting || parsed.length === 0}
          data-testid="attach-submit"
          onClick={check}
        >
          {submitting ? "Checking…" : "Check these accounts"}
        </button>
      )}
    </div>
  );
};

export default AccountsPanel;
